import React, { useState } from "react"
import RichTooltip from "~/ui-rtk/components/ui/common/RichTooltip"
import { TagInput, PrefixedInput } from "~/ui-rtk/components/atoms"
import {
  FormFieldGuide,
  GuideModal,
  PropertyTypeSelector,
} from "~/ui-rtk/components/molecules"
import type { AdditionalConfigField, PropertyTypeOption } from "../types"

interface ConfigFormFieldProps {
  config: AdditionalConfigField
  formData: Record<string, unknown>
  selectedPrefixes: Record<string, string>
  tagInputValues: Record<string, string>
  onInputChange: (fieldName: string, value: string | string[]) => void
  onPrefixChange: (fieldName: string, prefix: string) => void
  onTagInputChange: (fieldName: string, value: string) => void
  onRemoveTag: (fieldName: string, index: number) => void
  onAddTag: (fieldName: string, value: string) => void
  getTags: (fieldName: string) => string[]
}

/**
 * Renders a single configuration form field based on its type and options
 * Composes atoms (TagInput, PrefixedInput) and molecules (FormFieldGuide, GuideModal, PropertyTypeSelector)
 */
const ConfigFormField: React.FC<ConfigFormFieldProps> = ({
  config,
  formData,
  selectedPrefixes,
  tagInputValues,
  onInputChange,
  onPrefixChange,
  onTagInputChange,
  onRemoveTag,
  onAddTag,
  getTags,
}) => {
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false)

  const fieldName = config.configuration_path.split(".").pop()
  if (!fieldName) return null

  const value = formData[fieldName]

  // Get property type for fields with property_type_field
  const propertyType = config.property_type_field
    ? formData[config.property_type_field] ||
      Object.entries(config.property_type_options || {}).find(
        ([_, option]) => option.default === true,
      )?.[0] ||
      Object.keys(config.property_type_options || {})[0]
    : null

  const selectedTypeOption: PropertyTypeOption | null =
    propertyType && config.property_type_options
      ? config.property_type_options[propertyType as string]
      : null

  // Determine prefix to use/strip
  let prefixToStrip = ""
  if (selectedTypeOption?.prefix) {
    prefixToStrip = selectedTypeOption.prefix
  } else if (selectedTypeOption?.prefix_options?.length) {
    prefixToStrip = selectedPrefixes[fieldName] || ""
  }

  // Check if format is a comma-separated list (list_of_*)
  const isCommaSeparatedList = config.format.startsWith("list_of_")

  // Get display value (strip prefix for editing)
  let displayValue: string
  if (isCommaSeparatedList && Array.isArray(value)) {
    displayValue = value.map(item => String(item)).join(", ")
  } else {
    displayValue = Array.isArray(value)
      ? value.join(", ")
      : (value as string) || ""
  }

  if (prefixToStrip && displayValue && !isCommaSeparatedList) {
    if (displayValue.startsWith(prefixToStrip)) {
      displayValue = displayValue.substring(prefixToStrip.length)
    }
  }

  // Get tags for list format (strip prefix from each)
  const tags =
    config.format === "list"
      ? getTags(fieldName).map((tag: string) => {
          if (prefixToStrip && tag.startsWith(prefixToStrip)) {
            return tag.substring(prefixToStrip.length)
          }
          return tag
        })
      : []

  // Get placeholder and help text
  const getPlaceholder = () => {
    if (config.format === "list") {
      if (config.maxItems === 1) {
        return `Enter ${config.property_label.toLowerCase()}`
      }
      return "Type a value and click Add"
    }
    if (isCommaSeparatedList) {
      // Extract the type from format (e.g., "list_of_numbers" -> "numbers")
      const listType = config.format.replace("list_of_", "").replace("_", " ")
      return `Enter ${listType} (comma-separated)`
    }
    return `Enter ${config.property_label.toLowerCase()}`
  }

  const getHelpText = () => {
    if (config.format === "list") {
      if (config.maxItems === 1) {
        return `Enter a single ${config.property_label.toLowerCase()} and click Add.`
      }
      if (config.maxItems !== undefined) {
        return `Enter up to ${config.maxItems} values. Click Add after each one.`
      }
      return "Type a value and click Add to add it to the list."
    }
    if (isCommaSeparatedList) {
      // Use pattern_message if available, otherwise generate default help text
      if (config.pattern_message) {
        return config.pattern_message
      }
      const listType = config.format.replace("list_of_", "").replace("_", " ")
      return `Enter one or more ${listType} separated by commas`
    }
    return null
  }

  const helpText = getHelpText()

  // Render standalone dropdown when property_type_options exists WITHOUT property_type_field
  if (!config.property_type_field && config.property_type_options) {
    const selectValue =
      value ||
      Object.entries(config.property_type_options).find(
        ([_, option]) => option.default === true,
      )?.[0] ||
      Object.keys(config.property_type_options)[0]

    const selectedOption = config.property_type_options[selectValue as string]

    return (
      <div key={config.configuration_path} className="space-y-3">
        <div className="flex flex-col gap-2 justify-start">
          <div className="flex items-start gap-2">
            <label
              htmlFor={fieldName}
              className="text-base font-medium text-white text-left"
            >
              {config.property_label}*
            </label>
            {config.info && (
              <RichTooltip content={config.info} place="right">
                <svg
                  className="w-4 h-4 text-blue-400 cursor-help"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </RichTooltip>
            )}
            {config.guide && (
              <FormFieldGuide onClick={() => setIsGuideModalOpen(true)} />
            )}
          </div>

          <select
            id={fieldName}
            value={selectValue as string}
            onChange={e => onInputChange(fieldName, e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-basic-blue focus:border-basic-blue"
          >
            {Object.entries(config.property_type_options).map(
              ([optionKey, option]) => (
                <option key={optionKey} value={optionKey}>
                  {option.label}
                </option>
              ),
            )}
          </select>

          {selectedOption?.help_text && (
            <div className="text-xs text-cadet-blue mt-1">
              {selectedOption.help_text}
            </div>
          )}
        </div>

        {/* Guide Modal */}
        {config.guide && (
          <GuideModal
            isOpen={isGuideModalOpen}
            onClose={() => setIsGuideModalOpen(false)}
            title={`${config.property_label} Guide`}
            content={config.guide}
          />
        )}
      </div>
    )
  }

  // Standard field rendering
  return (
    <div key={config.configuration_path} className="space-y-3">
      {/* Property type selector (secondary dropdown + text input) */}
      {config.property_type_field && config.property_type_options && (
        <PropertyTypeSelector
          id={`${config.property_type_field}-select`}
          name={config.property_type_field}
          value={(propertyType as string) || ""}
          options={config.property_type_options}
          onChange={value => {
            if (config.property_type_field) {
              onInputChange(config.property_type_field, value)
            }
          }}
        />
      )}

      <div className="flex flex-col gap-2 justify-start">
        <div className="flex items-start gap-2">
          <label
            htmlFor={fieldName}
            className="text-base font-medium text-white text-left"
          >
            {config.property_label}*
          </label>
          {config.info && (
            <RichTooltip content={config.info} place="right">
              <svg
                className="w-4 h-4 text-blue-400 cursor-help"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </RichTooltip>
          )}
          {config.guide && (
            <FormFieldGuide onClick={() => setIsGuideModalOpen(true)} />
          )}
        </div>

        {/* Render input based on format and prefix type */}
        {config.format === "list" ? (
          <TagInput
            id={fieldName}
            value={tagInputValues[fieldName] || ""}
            tags={tags}
            placeholder={getPlaceholder()}
            prefix={selectedTypeOption?.prefix}
            prefixOptions={selectedTypeOption?.prefix_options}
            selectedPrefix={selectedPrefixes[fieldName]}
            onChange={value => onTagInputChange(fieldName, value)}
            onPrefixChange={prefix => onPrefixChange(fieldName, prefix)}
            onRemoveTag={index => onRemoveTag(fieldName, index)}
            onAddTag={() =>
              onAddTag(fieldName, tagInputValues[fieldName] || "")
            }
            maxItems={config.maxItems}
          />
        ) : (
          <PrefixedInput
            id={fieldName}
            value={displayValue}
            placeholder={getPlaceholder()}
            prefix={selectedTypeOption?.prefix}
            prefixOptions={selectedTypeOption?.prefix_options}
            selectedPrefix={selectedPrefixes[fieldName]}
            onChange={value => onInputChange(fieldName, value)}
            onPrefixChange={prefix => onPrefixChange(fieldName, prefix)}
          />
        )}

        {helpText && (
          <div className="text-xs text-cadet-blue mt-1">{helpText}</div>
        )}
      </div>

      {/* Guide Modal */}
      {config.guide && (
        <GuideModal
          isOpen={isGuideModalOpen}
          onClose={() => setIsGuideModalOpen(false)}
          title={`${config.property_label} Guide`}
          content={config.guide}
        />
      )}
    </div>
  )
}

export default ConfigFormField
